/**
 * BuyThesisDialog — modal for thesis buy with optimizer, deselection, and warnings.
 *
 * Features:
 * - Checkboxes to include/exclude each security
 * - "Optimize" button: calls portfolio optimizer for risk-adjusted weights
 * - Shares column showing computed share counts
 * - Concentration + correlation warnings
 * - Dollar / % NAV toggle with portfolio balancer recommendation
 */
import { useState, useEffect } from 'react';
import * as api from '../lib/api';

interface Props {
  symbols: string[];
  weights: number[];
  prices?: Record<string, number>;
  researchSessionId?: string;
  // v0.5.5 AE-076: caller supplies which mode is being committed
  // ("sim" or "live"). Default is 'sim' so historical callers keep paper.
  buyMode?: 'sim' | 'live';
  onClose: () => void;
  onSuccess: (result: any) => void;
}

export function BuyThesisDialog({ symbols, weights, prices, researchSessionId, buyMode = 'sim', onClose, onSuccess }: Props) {
  const [mode, setMode] = useState<'usd' | 'pct'>('usd');
  const [amount, setAmount] = useState<number>(10000);
  const [pctNav, setPctNav] = useState<number>(5);
  const [editableWeights, setEditableWeights] = useState<number[]>(weights);
  const [selected, setSelected] = useState<boolean[]>(symbols.map(() => true));
  const [busy, setBusy] = useState(false);
  const [optimizing, setOptimizing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recommendation, setRecommendation] = useState<any>(null);
  const [optimizeResult, setOptimizeResult] = useState<any>(null);
  const [stops, setStops] = useState<Record<string, number>>({});
  const [correlationFlags, setCorrelationFlags] = useState<string[]>([]);
  // v0.5.5 AE-076: LIVE confirmation modal (defense-in-depth on top of
  // the per-button label + backend mode guard).
  const [liveConfirm, setLiveConfirm] = useState(false);

  useEffect(() => {
    api.research.exposureRecommendation().then(setRecommendation).catch(() => {});
    api.research.atrStops(symbols).then((data) => {
      const stopMap: Record<string, number> = {};
      for (const s of data.stops) {
        stopMap[s.symbol] = s.recommended_stop_pct;
      }
      setStops(stopMap);
    }).catch(() => {});
    api.research.correlationMatrix(symbols).then((data) => {
      setCorrelationFlags(data.high_correlation_flags || []);
    }).catch(() => {});
  }, []);

  const activeSymbols = symbols.filter((_, i) => selected[i]);
  const activeWeights = editableWeights.filter((_, i) => selected[i]);
  const wSum = activeWeights.reduce((a, b) => a + b, 0);
  const effectiveExposure = mode === 'usd' ? amount : (pctNav / 100) * (recommendation?.nav_usd ?? 100000);

  async function handleOptimize() {
    setOptimizing(true);
    setError(null);
    setOptimizeResult(null);
    try {
      const result = await api.research.optimizeAllocation({
        symbols: activeSymbols,
        total_exposure_usd: effectiveExposure,
        method: 'max_sharpe_diversified',
      });
      setOptimizeResult(result);

      const newWeights = [...editableWeights];
      for (let i = 0; i < symbols.length; i++) {
        if (selected[i] && result.weights[symbols[i]] !== undefined) {
          newWeights[i] = result.weights[symbols[i]];
        }
      }
      setEditableWeights(newWeights);
    } catch (e: unknown) {
      setError((e as Error).message ?? 'Optimization failed');
    } finally {
      setOptimizing(false);
    }
  }

  async function handleBuy() {
    if (buyMode === 'live' && !liveConfirm) {
      setLiveConfirm(true);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const normalizedWeights = activeWeights.map(w => w / Math.max(wSum, 0.01));
      const activeStops: Record<string, number> = {};
      for (const sym of activeSymbols) {
        activeStops[sym] = stops[sym] ?? 5;
      }
      const result = await api.research.buyThesis({
        research_session_id: researchSessionId,
        symbols: activeSymbols,
        weights: normalizedWeights,
        stops: activeStops,
        total_exposure_usd: mode === 'usd' ? amount : null,
        total_exposure_pct_nav: mode === 'pct' ? pctNav : null,
        mode: buyMode,
      });
      if (result.error) {
        setError(result.error_detail ?? result.error);
        return;
      }
      onSuccess(result);
    } catch (e: unknown) {
      setError((e as Error).message ?? 'Order failed');
    } finally {
      setBusy(false);
      setLiveConfirm(false);
    }
  }

  function toggleSymbol(idx: number) {
    const newSelected = [...selected];
    newSelected[idx] = !newSelected[idx];
    setSelected(newSelected);
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-surface rounded-lg shadow-xl p-5 w-[560px] max-h-[85vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
          {buyMode === 'live' ? 'Buy LIVE — Real Schwab Order' : 'Buy SIM — Paper Trade'}
          <span
            className={
              buyMode === 'live'
                ? 'text-[10px] uppercase px-1.5 py-0.5 rounded bg-warn/15 text-warn'
                : 'text-[10px] uppercase px-1.5 py-0.5 rounded bg-edge text-ink-mute'
            }
            data-testid="buy-mode-badge"
          >
            {buyMode === 'live' ? 'live' : 'paper'}
          </span>
        </h3>

        {recommendation && recommendation.recommended_usd && (
          <div className="text-[11px] text-ink-mute bg-accent/5 rounded p-2 mb-3">
            Recommended: ${recommendation.recommended_usd?.toLocaleString()} ({recommendation.recommended_pct_nav?.toFixed(1)}% of NAV based on portfolio balance)
          </div>
        )}

        <div className="mb-3">
          <div className="flex gap-2 mb-2">
            <button
              className={`text-xs px-3 py-1 rounded ${mode === 'usd' ? 'bg-accent text-white' : 'bg-edge'}`}
              onClick={() => setMode('usd')}
            >
              Dollar Amount
            </button>
            <button
              className={`text-xs px-3 py-1 rounded ${mode === 'pct' ? 'bg-accent text-white' : 'bg-edge'}`}
              onClick={() => setMode('pct')}
            >
              % of NAV
            </button>
          </div>
          {mode === 'usd' ? (
            <input
              type="number"
              className="input w-full"
              value={amount}
              onChange={(e) => setAmount(Number(e.target.value))}
              min={100}
              step={500}
            />
          ) : (
            <input
              type="number"
              className="input w-full"
              value={pctNav}
              onChange={(e) => setPctNav(Number(e.target.value))}
              min={0.5}
              max={50}
              step={0.5}
            />
          )}
        </div>

        <table className="w-full text-xs mb-3">
          <thead className="text-ink-mute">
            <tr>
              <th className="text-center py-1 w-8"></th>
              <th className="text-left py-1">Symbol</th>
              <th className="text-right py-1">Weight %</th>
              <th className="text-right py-1">Allocation</th>
              <th className="text-right py-1">Shares</th>
              <th className="text-right py-1">Stop %</th>
            </tr>
          </thead>
          <tbody>
            {symbols.map((sym, i) => {
              const isActive = selected[i];
              const w = isActive ? editableWeights[i] / Math.max(wSum, 0.01) : 0;
              const alloc = effectiveExposure * w;
              const price = prices?.[sym] || optimizeResult?.shares?.[sym] ? effectiveExposure * (editableWeights[i] / Math.max(wSum, 0.01)) / Math.max(optimizeResult?.shares?.[sym] || 1, 1) : 100;
              const shareCount = optimizeResult?.shares?.[sym] ?? (price > 0 ? Math.floor(alloc / price) : 0);

              return (
                <tr key={sym} className={`border-t border-edge ${!isActive ? 'opacity-40' : ''}`}>
                  <td className="text-center py-1">
                    <input
                      type="checkbox"
                      checked={isActive}
                      onChange={() => toggleSymbol(i)}
                      className="w-3.5 h-3.5"
                    />
                  </td>
                  <td className="py-1 font-semibold">{sym}</td>
                  <td className="text-right">
                    <input
                      type="number"
                      className="input w-14 text-right text-xs"
                      value={Math.round(editableWeights[i] * 100)}
                      onChange={(e) => {
                        const newW = [...editableWeights];
                        newW[i] = Number(e.target.value) / 100;
                        setEditableWeights(newW);
                      }}
                      min={0}
                      max={100}
                      disabled={!isActive}
                    />
                  </td>
                  <td className="text-right text-ink-mute">
                    ${alloc.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </td>
                  <td className="text-right num font-semibold">
                    {isActive ? shareCount : '—'}
                  </td>
                  <td className="text-right">
                    <input
                      type="number"
                      className="input w-14 text-right text-xs"
                      value={Math.round(stops[sym] ?? 5)}
                      onChange={(e) => setStops({ ...stops, [sym]: Number(e.target.value) })}
                      min={1}
                      max={50}
                      disabled={!isActive}
                      title="ATR-based stop-loss %"
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {correlationFlags.length > 0 && (
          <div className="text-[11px] bg-amber-50 border border-amber-200 rounded p-2 mb-3">
            <div className="font-semibold text-amber-800 mb-1">Correlation Warnings</div>
            {correlationFlags.map((f, i) => (
              <div key={i} className="text-amber-700">⚠ {f}</div>
            ))}
          </div>
        )}

        {optimizeResult && (
          <div className="text-[11px] bg-emerald-50 border border-emerald-200 rounded p-2 mb-3 space-y-1">
            <div className="font-semibold text-emerald-800">Optimized Allocation</div>
            {optimizeResult.expected_sharpe && (
              <div>Expected Sharpe: <span className="num">{optimizeResult.expected_sharpe.toFixed(2)}</span></div>
            )}
            {optimizeResult.correlation_with_portfolio != null && (
              <div>
                Portfolio correlation: <span className="num">{optimizeResult.correlation_with_portfolio.toFixed(2)}</span>
                {' '}— diversification: <span className="font-semibold">{optimizeResult.diversification_benefit}</span>
              </div>
            )}
            {optimizeResult.flags?.length > 0 && (
              <div className="text-amber-700">
                {optimizeResult.flags.map((f: string, i: number) => (
                  <div key={i}>⚠ {f}</div>
                ))}
              </div>
            )}
            <div className="text-ink-mute italic">{optimizeResult.reasoning}</div>
          </div>
        )}

        {error && <div className="text-bad text-xs mb-2" data-testid="buy-error">{error}</div>}

        {liveConfirm && buyMode === 'live' && (
          <div className="card border-warn p-3 mb-3" data-testid="live-confirm-card">
            <div className="text-warn font-semibold text-xs mb-1">Confirm LIVE order</div>
            <div className="text-[11px] text-ink-mute">
              This will place REAL market orders on your Schwab account for {activeSymbols.length} symbol{activeSymbols.length !== 1 ? 's' : ''} totaling approximately ${effectiveExposure.toLocaleString(undefined, { maximumFractionDigits: 0 })}.
              The orders go to the pending-approval queue first; you'll still get a final review in the morning brief.
            </div>
          </div>
        )}

        <div className="flex gap-2 justify-between">
          <button
            className="btn text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            onClick={handleOptimize}
            disabled={optimizing || activeSymbols.length === 0}
          >
            {optimizing ? 'Optimizing…' : '⚡ Optimize'}
          </button>
          <div className="flex gap-2">
            <button className="btn text-xs" onClick={onClose}>Cancel</button>
            <button
              className={
                buyMode === 'live'
                  ? 'btn-primary text-xs bg-warn hover:opacity-90'
                  : 'btn-primary text-xs bg-emerald-600 hover:bg-emerald-700'
              }
              onClick={handleBuy}
              disabled={busy || activeSymbols.length === 0}
              data-testid="buy-confirm-button"
            >
              {busy
                ? 'Placing orders…'
                : buyMode === 'live'
                  ? liveConfirm
                    ? `Confirm LIVE Buy (${activeSymbols.length})`
                    : `Buy LIVE (${activeSymbols.length})`
                  : `Buy SIM (${activeSymbols.length})`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
